import React, { useState, useRef } from 'react'
import {
  Accordion,
  Button,
  Drawer,
  Grid,
  Heading,
  Input,
  Separator,
  useToastAlert,
} from '@design-systems/canvas-ui'
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMeh, faSmile, faFrown } from '@fortawesome/free-regular-svg-icons'
import { useAuth } from 'properties-ui-common'
import apiConfig from '../../../../apiConfig'
import './FeedbackWidget.css'

interface FeedbackWidgetProps {
  isOpen: boolean
  onFeedback: () => void
}

interface Rating {
  title: 'Feedback' | 'Issue' | 'Idea' | 'Comment'
  value: string | null
}

export const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({
  isOpen,
  onFeedback,
}) => {
  const auth = useAuth()
  const makeToast = useToastAlert()
  const currPage = window.location.href
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [feedbackValue, setFeedbackValue] = useState('')
  const [issueValue, setIssueValue] = useState('')
  const [ideaValue, setIdeaValue] = useState('')
  const [commentValue, setCommentValue] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])

  const ratings: Rating[] = [
    { title: 'Issue', value: issueValue },
    { title: 'Idea', value: ideaValue },
    { title: 'Comment', value: commentValue },
    { title: 'Feedback', value: feedbackValue },
  ]

  const showToaster = () => {
    makeToast({
      heading: 'Request Failed',
      variant: 'error',
      autoHideDuration: 5000,
      message: 'Please try again later',
    })
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const newFiles = Array.from(event.target.files)
      setAttachments((prev) => [...prev, ...newFiles])
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const removeFile = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const sendToSlack = async () => {
    try {
      const feedbackData = {
        blocks: [
          {
            type: 'section',
            text: {
              type: 'mrkdwn',
              text: `*User*: ${
                auth?.session?.userInfo?.fullName ?? ''
              } \n*Email*: ${
                auth?.session?.userInfo?.email ?? ''
              } \n*Path*: ${currPage} \n*Date:* ${Date()}`,
            },
          },
          {
            type: 'section',
            text: {
              type: 'mrkdwn',
              text: `${ratings
                .filter((arr) => arr.value)
                .map((feedback) => `*${feedback.title}* : _${feedback.value}_`)
                .join('\n')}`,
            },
          },
        ],
      }

      const feedbackJson = JSON.stringify(feedbackData)

      if (attachments.length === 0) {
        // No attachments - use JSON endpoint
        // Let the global Praxis axios interceptor inject the Authorization header.
        await axios.post(apiConfig.webhooks.slack, feedbackJson, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
      } else {
        // With attachments - use multipart/form-data endpoint
        const formData = new FormData()
        formData.append('feedback', feedbackJson)

        attachments.forEach((file) => {
          formData.append('files', file)
        })

        await axios.post(apiConfig.webhooks.slack, formData)
      }

      onFeedback()
    } catch (err) {
      showToaster()
      console.error('Failed to post feedback:', err)
    }
  }

  const footerContent = (
    <Grid.Container spacing="dense" direction="row-reverse">
      <Grid.Item>
        <Button
          variant="primary"
          isDisabled={
            !feedbackValue && !commentValue && !issueValue && !ideaValue
          }
          onClick={sendToSlack}
        >
          Submit
        </Button>
      </Grid.Item>
      <Grid.Item>
        <Button variant="destructive" onClick={onFeedback}>
          Cancel
        </Button>
      </Grid.Item>
    </Grid.Container>
  )

  return (
    <Drawer
      isOpen={isOpen}
      onRequestClose={onFeedback}
      headingText="Provide Feedback"
      footerContent={footerContent}
      location="right"
      spacing="normal"
    >
      <Accordion
        header="Report an Issue"
        data-testid="feedback-widget-expandable-section-issue"
        preserveDom
      >
        <Input.Textarea
          id="report-issue-textbox"
          data-testid="issue"
          placeholder="What's the issue?"
          value={issueValue}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
            setIssueValue(event.target.value)
          }
        />
      </Accordion>
      <Separator className="hc8-mv-4x" />
      <Accordion
        header="Share an Idea"
        data-testid="feedback-widget-expandable-section-idea"
        preserveDom
      >
        <Input.Textarea
          id="share-idea-textbox"
          data-testid="idea"
          placeholder="What's your idea?"
          value={ideaValue}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
            setIdeaValue(event.target.value)
          }
        />
      </Accordion>
      <Separator className="hc8-mv-4x" />
      <Accordion
        header="Overall Rating"
        data-testid="feedback-widget-expandable-section-feedback"
        preserveDom
      >
        <div className="hc-mv-dense">
          <label>How do you feel?</label>
          <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
            {[
              { value: 'happy', icon: faSmile, label: 'Happy' },
              { value: 'neutral', icon: faMeh, label: 'Neutral' },
              { value: 'unhappy', icon: faFrown, label: 'Unhappy' },
            ].map(({ value, icon, label }) => (
              <label
                key={value}
                style={{ cursor: 'pointer', textAlign: 'center' }}
              >
                <input
                  type="radio"
                  name="feedback-radio"
                  value={value}
                  checked={feedbackValue === value}
                  onChange={() => setFeedbackValue(value)}
                  style={{ display: 'none' }}
                />
                <FontAwesomeIcon
                  icon={icon as any}
                  size="2x"
                  style={{
                    color: feedbackValue === value ? '#CC0000' : undefined,
                  }}
                />
                <div>{label}</div>
              </label>
            ))}
          </div>
        </div>
        <Input.Textarea
          id="comment-textbox"
          data-testid="comment"
          placeholder="Comments"
          value={commentValue}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
            setCommentValue(event.target.value)
          }
        />
      </Accordion>
      <Separator className="hc8-mv-4x" />
      <div className="hc-mv-dense">
        <Heading size={6} className="hc-mb-dense">
          Attach Files
        </Heading>
        <input
          ref={fileInputRef}
          id="file-upload"
          type="file"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <Button
          size="sm"
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
        >
          Choose Files
        </Button>
        {attachments.length > 0 && (
          <div className="hc-mt-normal">
            <strong>Selected Files:</strong>
            <ul>
              {attachments.map((file, index) => (
                <li key={index}>
                  {file.name}{' '}
                  <Button size="sm" onClick={() => removeFile(index)}>
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Drawer>
  )
}
